import { getToken, clearToken } from '../utils/authToken';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Trash2, Download, Copy, Check, FolderDown, FileText, Play, Share2, X, Zap, ArrowLeft, MessageSquare, Sparkles, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { navigateToPremium } from '../utils/navigateToPremium';
import axios from 'axios';
import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import Avatar from './Avatar';
import ShareConversationModal from './ShareConversationModal';
import RollbackModal from './RollbackModal';
import StyleIndicator from './StyleIndicator';
import ActiveModulesIndicator from './ActiveModulesIndicator';
import ActiveModulesPanel from './ActiveModulesPanel';
import HomepageModal from './HomepageModal';
import { downloadConversationAsProject } from '../utils/downloadProject';
import { exportConversationToPDF } from '../utils/exportPDF';
import { useLanguage } from '../context/LanguageContext';
import { 
  WorkflowPanel, 
  ProductivityPanel, 
  MindPanelHeader
} from './WorkspaceSync';

import { BACKEND_URL, API_URL } from '../config/api';
import { isDamyxActionBlock } from '../utils/damyxFence';
import chatPanelTranslations from './chatPanelTranslations';

// Subcomponentes extraídos en el refactor P3.1 sesión 3/4 (feb-2026).
// NOTA: streaming, fetch, AbortController y toda la lógica de envío
// se mantiene en ChatPanel.jsx (regla del cuadro negro).
import ChatToolbar from './chat/ChatToolbar';
import ChatMessages from './chat/ChatMessages';
import BuilderToggle from './BuilderToggle';

// API endpoint
const API = API_URL;

const ChatPanel = ({ 
  conversations,
  currentConversation, 
  onSelectConversation,
  onDeleteConversation,
  onCloseConversation,
  onSendMessage,
  onRefreshConversation,
  onNewConversation,
  onUpdateMessages,
  searchQuery,
  onSearchChange,
  onOpenGroupsProjects,
  onOpenServicesInfo,
  onOpenStepPanel,
  // Props para IdeaLightbulb
  onAttachFile,
  onOpenImageGenerator,
  onOpenDiagramGenerator,
  onInstallApp,
  onOpenImageGallery,
  // Props para abrir modal de conversaciones
  onOpenConversationsModal,
  // Props para Style Indicator
  currentProfile,
  styleWasManual,
  levelWasManual,
  // Props para Active Modules Indicator
  activeModules,
  // Callback para ejecutar código
  onGoToWorkspace,
  // Props para expandir panel
  onExpandPanel,
  isExpanded = false,
  // Fase 12.1: módulo activo (Workspace ↔ Chat sync). Si está presente, el
  // botón "Revertir" hace undo de ese módulo en lugar de abrir el snapshot picker.
  activeModuleId = null,
}) => {
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [workflowStep, setWorkflowStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [productivityMetrics, setProductivityMetrics] = useState({
    components: 0,
    fixes: 0,
    lines: 0,
    time: '0s'
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedConvId, setCopiedConvId] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [previewCode, setPreviewCode] = useState('');
  const [previewLanguage, setPreviewLanguage] = useState('jsx');
  const messagesEndRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  
  // Ref to flag "expecting a new response" - avoids flash of full content
  const awaitingResponseRef = useRef(false);
  
  // Estados para paneles colapsables (móvil y desktop)
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  
  // Estados para paneles colapsables en desktop
  const [showWorkflowDesktop, setShowWorkflowDesktop] = useState(false);
  const [showMetricsDesktop, setShowMetricsDesktop] = useState(false);

  // 🟥 P23 · Toggle Builder ON/OFF por proyecto.
  // Fetch inicial cuando cambia el módulo activo. Default OFF.
  const [builderEnabled, setBuilderEnabled] = useState(false);
  // iter453 · Nombre del proyecto activo para el botón "Nuevo Proyecto".
  const [activeModuleName, setActiveModuleName] = useState('');
  useEffect(() => {
    if (!activeModuleId) {
      setBuilderEnabled(false);
      setActiveModuleName('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('damyxia_token');
        const res = await axios.get(
          `${API}/damyxia/modules/${activeModuleId}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : {},
        );
        if (!cancelled) {
          setBuilderEnabled(Boolean(res.data?.builder_enabled));
          setActiveModuleName(String(res.data?.name || ''));
        }
      } catch {
        if (!cancelled) {
          setBuilderEnabled(false);
          setActiveModuleName('');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeModuleId]);
  
  // Estado para el panel de módulos activos
  const [showModulesPanel, setShowModulesPanel] = useState(false);
  
  // Estado para el modal de Rollback
  const [showRollbackModal, setShowRollbackModal] = useState(false);

  // ─── FASE 13: Chat colapsable automático ────────────────────────────
  // Solo UN mensaje está expandido a la vez. Default: el último.
  // 🟥 CUADRO NEGRO DEFINITIVO 2026-06-27 (rev6) — "Últimos 3 abiertos +
  // historial oculto pero accesible".
  //   - Por defecto: SOLO los últimos 3 mensajes están expandidos.
  //   - Los mensajes anteriores quedan ocultos pero accesibles vía botón "Ver".
  //   - Si el usuario abre uno antiguo manualmente, queda en `userOpened`
  //     y SE PRESERVA aunque lleguen mensajes nuevos.
  //   - Si el usuario cierra uno de los últimos 3, queda en `userClosed`
  //     hasta que llegue un mensaje nuevo (entonces se vuelve a abrir).
  //   - Al cambiar de conversación, ambos sets se resetean.
  const LAST_OPEN_COUNT = 3;
  const [userOpened, setUserOpened] = useState(() => new Set()); // sobrevive a mensajes nuevos
  const [userClosed, setUserClosed] = useState(() => new Set()); // se resetea al llegar mensaje nuevo
  const prevMsgLenRef = useRef(0);
  const prevConvIdRef = useRef(null);

  // ─── FASE 13.1: Mini-indicadores de contenido en pills ──────────────
  // Detecta el tipo de contenido (code/image/json/longText) para mostrar
  // un emoji-icono en el pill colapsado. Función PURA, sin side-effects.
  // Prioridad: code(⚙️) > image(📷) > json(🧩) > longText(📝).
  const detectMessageIcon = (content) => {
    if (!content || typeof content !== 'string') return null;
    // 1) Code blocks (```...```).
    if (/```[\s\S]*?```/.test(content)) return '⚙️';
    // 2) Imágenes en markdown ![](url) o data:image/.
    if (/!\[.*?\]\(.*?\)/.test(content) || /data:image\//.test(content)) return '📷';
    // 3) JSON válido (objeto top-level).
    const trimmed = content.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        JSON.parse(trimmed);
        return '🧩';
      } catch (_) { /* not valid json */ }
    }
    // 4) Texto largo.
    if (content.length > 180) return '📝';
    return null;
  };

  // ─── FASE 13.3: Tooltip descriptivo del icono detectado ─────────────
  // Devuelve el texto que aparece en el `title` nativo del pill al hacer
  // hover. Bilingüe (es/en).
  const getIconTooltip = (icon) => {
    const map = language === 'es'
      ? {
          '⚙️': 'tu mensaje tiene código',
          '📷': 'tu mensaje contiene imagen',
          '🧩': 'tu mensaje contiene JSON',
          '📝': 'tu mensaje es texto largo',
        }
      : {
          '⚙️': 'this message contains code',
          '📷': 'this message contains an image',
          '🧩': 'this message contains JSON',
          '📝': 'this message is long text',
        };
    return map[icon] || '';
  };

  // Fase 12.1: estado del botón "Revertir" — evita doble-click durante la
  // llamada al endpoint de undo por módulo.
  const [reverting, setReverting] = useState(false);
  // 🟥 P18.1 2026-07-25 — Guard SINCRÓNICO contra doble-click. setReverting es
  // async y no bloquea clicks rápidos consecutivos → Marcelo tocó Revertir
  // varias veces y borró 3 turnos en cadena. Un ref bloquea instantáneo.
  const revertingRef = useRef(false);

  // 🟦 V18.7 — Chat Auto-Snapshots: cache del count por conversación.
  // El botón "Revertir" se activa SOLO si count > 0.
  const [chatSnapshotCount, setChatSnapshotCount] = useState(0);

  const refreshChatSnapshotCount = async (convId) => {
    if (!convId) { setChatSnapshotCount(0); return 0; }
    try {
      // 🟥 CUADRO NEGRO DEFINITIVO 2026-02-26 — Cookie httpOnly es la única
      // fuente de sesión. NO leemos localStorage('access_token'|'token').
      const r = await fetch(`${API}/snapshots/chat-auto/by-conversation/${convId}`, {
        credentials: 'include',
      });
      if (!r.ok) { setChatSnapshotCount(0); return 0; }
      const d = await r.json();
      const c = Number(d?.count || 0);
      setChatSnapshotCount(c);
      return c;
    } catch {
      setChatSnapshotCount(0);
      return 0;
    }
  };

  const createChatSnapshot = async (convId, kind, summary, moduleId = null) => {
    if (!convId) return;
    try {
      // 🟥 CUADRO NEGRO DEFINITIVO 2026-02-26 — Sin reads a localStorage de sesión.
      // 🟥 P17 2026-07-25 · Ahora también envía module_id opcional al backend.
      const body = { conversation_id: convId, kind, summary };
      if (moduleId) body.module_id = moduleId;
      await fetch(`${API}/snapshots/chat-auto/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
    } catch { /* best-effort */ }
  };

  /**
   * Fase 12.1 + revert-button-undo (feb-2026):
   * 🟥 2026-02-06 · Marcelo pidió que "Revertir" ejecute en PARALELO:
   *   (a) undo del último turno del chat (borra user+assistant y refresca UI)
   *   (b) undo de la última edición del módulo activo (workspace)
   * Antes era if/else — ahora ambas corren juntas para que un solo clic
   * revierta "el último mensaje" Y "la última ejecución visual" a la vez.
   * Ambos botones (el del chat y el amarillo del workspace, ahora al lado
   * de Tienda) llaman a este mismo handler.
   */
  const handleRevertClick = async () => {
    setShowMobileFunctions(false);
    // 🟥 P18.1 · Guard sincrónico anti doble-click. Ver comentario en useRef.
    if (revertingRef.current || reverting) return;
    revertingRef.current = true;
    setReverting(true);

    try {
      const hasTurnUndo = Boolean(
        currentConversation?.id && (currentConversation?.messages || []).length > 0
      );
      const hasModuleUndo = Boolean(activeModuleId);

      // Nada que hacer → fallback a snapshots.
      if (!hasTurnUndo && !hasModuleUndo) {
        const liveCount = await refreshChatSnapshotCount(currentConversation?.id);
        if (liveCount > 0) {
          setShowRollbackModal(true);
        } else {
          toast.info(
            language === 'es'
              ? 'No hay nada que revertir en esta conversación.'
              : 'Nothing to undo in this conversation.',
            { duration: 3500 },
          );
        }
        return;
      }

      // Ejecutar ambos undos en paralelo (Promise.allSettled — un fallo no
      // aborta al otro).
      const results = await Promise.allSettled([
        hasTurnUndo ? _undoLastChatTurn() : Promise.resolve(null),
        hasModuleUndo
          ? (async () => {
              const res = await axios.post(
                `${API}/damyxia/modules/${activeModuleId}/undo`,
                { conversation_id: currentConversation?.id || null },
                { withCredentials: true },
              );
              const data = res?.data || {};
              try {
                window.dispatchEvent(new CustomEvent('damyxia:internal-module-updated', {
                  detail: { moduleId: activeModuleId, action: 'undo' },
                }));
              } catch (_) { /* noop */ }
              if (currentConversation?.id && onRefreshConversation) {
                try { await onRefreshConversation(currentConversation.id); } catch (_) { /* noop */ }
              } else if (data.appended_message && currentConversation && onUpdateMessages) {
                onUpdateMessages([
                  ...(currentConversation.messages || []),
                  data.appended_message,
                ]);
              }
              return data;
            })()
          : Promise.resolve(null),
      ]);

      const [turnRes, moduleRes] = results;
      if (hasModuleUndo && moduleRes.status === 'fulfilled') {
        toast.success(
          language === 'es'
            ? '✓ Edición del workspace revertida'
            : '✓ Workspace edit reverted',
          { duration: 3500 }
        );
      } else if (hasModuleUndo && moduleRes.status === 'rejected') {
        // Si el módulo no tenía historial (404), es info; cualquier otro error → error
        const st = moduleRes.reason?.response?.status;
        if (st === 404) {
          // no ruido — probable que solo se revirtió el chat
        } else {
          toast.error(language === 'es' ? 'Error al revertir el workspace' : 'Workspace undo failed');
        }
      }
    } finally {
      // Liberar guards SIEMPRE, aunque cualquier rama falle.
      revertingRef.current = false;
      setReverting(false);
    }
  };

  /**
   * Undo del último turno del chat actual (revert-button-undo, feb-2026).
   *
   * 🟥 P18 2026-07-25 — Marcelo reportó que "Revertir" no funcionaba pero
   * borrar el último mensaje del chat SÍ removía el cuadro creado por
   * DamyxIA. Root cause: la lógica anterior dependía 100% del endpoint
   * `/undo-last` y si tenía cualquier hipo (404, timing, cache) mostraba
   * el modal vacío y confundía al usuario.
   *
   * Nueva estrategia (idéntica al comportamiento de borrar mensaje):
   *   1. UI INSTANTÁNEA: recortar `messages.slice(0, -N)` localmente y
   *      llamar `onUpdateMessages(nuevo_array)` — los cuadros creados por
   *      DamyxIA desaparecen de inmediato porque se renderizan a partir
   *      de esos mensajes.
   *   2. PERSISTIR en background: llamar POST /undo-last para que el
   *      backend elimine los mismos mensajes de la DB. Si falla → no
   *      bloqueamos al usuario (el frontend ya reflejó el undo).
   */
  const _undoLastChatTurn = async () => {
    const messages = currentConversation?.messages || [];
    const msgCount = messages.length;

    if (!currentConversation?.id || msgCount === 0) {
      toast.info(
        language === 'es'
          ? 'No hay nada que revertir en esta conversación.'
          : 'Nothing to undo in this conversation.',
        { duration: 3500 },
      );
      return;
    }

    // Cálculo del turno a remover: igual que en backend
    // (assistant+user seguidos → 2; caso raro → 1)
    let removeCount = 1;
    if (msgCount >= 2) {
      const last = messages[msgCount - 1];
      const prev = messages[msgCount - 2];
      if (last?.role === 'assistant' && prev?.role === 'user') {
        removeCount = 2;
      }
    }

    // 🟥 P18.1 · Guard reverting ya está seteado por el caller (handleRevertClick).
    // No lo tocamos acá para no romper la protección anti doble-click.

    // 1) UI instantánea — comportamiento "borrar último mensaje"
    const trimmedMessages = messages.slice(0, msgCount - removeCount);
    if (onUpdateMessages) {
      onUpdateMessages(trimmedMessages);
    }
    toast.success(
      language === 'es'
        ? `✓ Última acción revertida (${removeCount} mensaje${removeCount === 1 ? '' : 's'} eliminado${removeCount === 1 ? '' : 's'})`
        : `✓ Last action undone (${removeCount} message${removeCount === 1 ? '' : 's'} removed)`,
      { duration: 3500 },
    );

    // 2) Persistir en background (await). Bloquear guard hasta que backend responda
    // evita que un click subsecuente ejecute un segundo undo antes de que el
    // primero termine y llegue al DB.
    try {
      await axios.post(
        `${API}/conversations/${currentConversation.id}/undo-last`,
        {},
        { withCredentials: true },
      );
    } catch (err) {
      console.warn('[revert-btn] persist undo failed (UI already reflected):', err?.response?.status);
    }
  };
  
  // Estado para el menú Funciones en móvil
  const [showMobileFunctions, setShowMobileFunctions] = useState(false);
  
  // Estado para el modal de Homepage
  const [showHomepageModal, setShowHomepageModal] = useState(false);
  
  // Estado para Drag & Drop de imágenes
  const [isDragging, setIsDragging] = useState(false);
  const [draggedImage, setDraggedImage] = useState(null);
  const dropZoneRef = useRef(null);
  
  // Modo desarrollador - oculta paneles de debug por defecto
  // Para activar: localStorage.setItem('damyxia-dev-mode', 'true') y recargar
  const [devModeEnabled] = useState(() => {
    return localStorage.getItem('damyxia-dev-mode') === 'true';
  });

  // Textos traducidos
  const t = (chatPanelTranslations[language] || chatPanelTranslations.es);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentConversation]);
  // 🟦 V18.7 — Refresh chat snapshot count cuando cambia la conversación.
  useEffect(() => {
    refreshChatSnapshotCount(currentConversation?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversation?.id]);
  // Detect new assistant messages for typewriter effect
  useEffect(() => {
    if (!awaitingResponseRef.current) return;
    const msgs = currentConversation?.messages || [];
    if (msgs.length > 0 && msgs[msgs.length - 1]?.role === 'assistant') {
      // Reset after a delay to allow StreamingText to complete
      setTimeout(() => { awaitingResponseRef.current = false; }, 15000);
    }
  }, [currentConversation?.messages]);

  // 🟥 2026-02-06 · Listener del evento global `damyxia:revert-both`. El
  // botón amarillo "Revertir" del Workspace (ahora al lado de "Tienda")
  // dispara este evento; ChatPanel corre `handleRevertClick` que hace
  // ambos undos en paralelo (chat turn + module edit).
  useEffect(() => {
    const handler = () => { handleRevertClick(); };
    window.addEventListener('damyxia:revert-both', handler);
    return () => window.removeEventListener('damyxia:revert-both', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversation?.id, activeModuleId]);


  // 🟥 P17 2026-07-25 · Heurística "mensaje modifica proyecto".
  // Antes solo se disparaban snapshots si había `activeModuleId`. P17 exige
  // cubrir cambios en Vista Previa React y Workspace Interno donde no siempre
  // hay un módulo explícito.
  // Estrategia: keywords de intención mutante + target de UI/código. El dedupe
  // <1s del backend + delete endpoint protegen de ruido. Preferimos falsos
  // positivos antes que perder un snapshot legítimo (P17 exige "TODA acción").
  //
  // NOTA: usamos lookarounds con \p{L} (Unicode letters) porque \b en JS trata
  // vocales acentuadas (á/é/í/ó/ú/ñ) como no-word y rompe boundaries.
  const isMutatingIntent = (msg) => {
    if (!msg || typeof msg !== 'string') return false;
    const m = msg.toLowerCase();
    // "verb" al principio o después de no-letra. Cubre agregá, modificá, borrá, etc.
    const verb = /(?:^|[^\p{L}])(crea|creá|creas|crear|creo|cambia|cambiá|cambiar|cambiás|cambio|agrega|agregá|agregar|agregás|añade|añadí|añadir|borra|borrá|borrar|elimina|eliminá|eliminar|modifica|modificá|modificar|arregla|arreglá|arreglar|corregí|corregir|corrige|diseña|diseñá|diseñar|edita|editá|editar|reemplaza|reemplazá|reemplazar|haz|hace|hacé|hacer|construí|construye|construir|create|add|remove|delete|change|modify|edit|fix|design|replace|build|make|update|rebuild)(?:[^\p{L}]|$)/u;
    const target = /(bot[oó]n|button|color|fondo|background|texto|text|componente|component|c[oó]digo|code|css|tailwind|jsx|estilo|style|preview|workspace|m[oó]dulo|module|\bui\b|interfaz|layout|panel|header|footer|men[uú])/iu;
    return verb.test(m) && target.test(m);
  };

  const handleSendMessage = async (message, options = {}) => {
    if (!message.trim()) return;

    setIsLoading(true);
    awaitingResponseRef.current = true;

    // 🟥 P17 · Snapshots BEFORE/AFTER en toda acción que modifique el proyecto.
    // Trigger si: (a) hay módulo activo, o (b) el mensaje tiene intent mutante
    // (kwds "crear/cambiar/agregar" + target "botón/color/código/…"). El dedupe
    // <1s del backend + eliminar snapshots corruptos protegen de ruido.
    // P22: si el usuario forzó CATEGORÍA A (solo texto), NO tocamos snapshots
    // — no vamos a modificar nada del proyecto.
    const convId = currentConversation?.id;
    const isForcedTextOnly = options.forceCategory === 'A';
    const modifiesProject =
      !isForcedTextOnly &&
      Boolean(convId) &&
      (Boolean(activeModuleId) || isMutatingIntent(message));
    if (modifiesProject) {
      await createChatSnapshot(
        convId,
        'before',
        `Antes de: ${message.slice(0, 60)}`,
        activeModuleId,
      );
    }

    try {
      await onSendMessage(message, options);
    } catch (error) {
      console.error('Error sending message:', error);
      if (!error.response) {
        toast.error(t.errorSendMessage);
      }
      awaitingResponseRef.current = false;
    } finally {
      setIsLoading(false);
      // Snapshot "after" + refresh del contador (best-effort).
      if (modifiesProject) {
        await createChatSnapshot(
          convId,
          'after',
          `Después de: ${message.slice(0, 60)}`,
          activeModuleId,
        );
        refreshChatSnapshotCount(convId);
      }
    }
  };

  // P22 · Callback para los botones inline A/B del clasificador de intención.
  // Reenvía el mensaje original con `force_category` para que el backend
  // ejecute el flujo correcto sin re-clasificar.
  const handleIntentDecision = (originalMessage, forceCategory) => {
    if (!originalMessage) return;
    handleSendMessage(originalMessage, { forceCategory });
  };

  // "Leer imagen" — envia la imagen al chat con vision_mode=true para que
  // GPT-4o lea/transcriba el contenido. Reusa el sendMessage del App (que ya
  // sabe inyectar vision_mode + image_base64 al body de /api/chat).
  const handleReadImageWithVision = async ({ imageBase64, mimeType, filename, message }) => {
    if (!imageBase64) return;
    setIsLoading(true);
    awaitingResponseRef.current = true;

    // Optimistic update: muestra el turno del usuario con preview de la imagen
    const previewDataUrl = `data:${mimeType || 'image/png'};base64,${imageBase64}`;
    const imgLabel = language === 'es' ? 'Leer imagen' : 'Read image';
    const tempUserMessage = {
      role: 'user',
      content: `[📷 ${imgLabel}: ${filename || 'image'}]\n${message}`,
      timestamp: new Date().toISOString(),
      has_image: true,
      image_base64_preview: previewDataUrl,
    };
    if (currentConversation && onUpdateMessages) {
      const tempMessages = [...(currentConversation.messages || []), tempUserMessage];
      onUpdateMessages(tempMessages);
    }

    try {
      await onSendMessage(message, {
        visionMode: true,
        imageBase64,
        mimeType: mimeType || 'image/png',
      });
    } catch (error) {
      console.error('Error reading image with vision:', error);
      const status = error?.response?.status;
      const detailRaw = error?.response?.data?.detail;
      const isPremiumLock = status === 403 && (
        (typeof detailRaw === 'object' && detailRaw?.code === 'PREMIUM_REQUIRED') ||
        (typeof detailRaw === 'string' && /premium/i.test(detailRaw))
      );
      if (isPremiumLock) {
        toast.error(
          language === 'es'
            ? 'Leer imagen con GPT-4o es exclusivo Premium. Actualiza tu plan.'
            : 'Reading images with GPT-4o is Premium-only. Upgrade your plan.',
          {
            duration: 6000,
            action: {
              label: language === 'es' ? 'Actualizar' : 'Upgrade',
              onClick: navigateToPremium,
            },
          }
        );
      } else {
        const detail = typeof detailRaw === 'object' ? (detailRaw?.message || 'unknown') : (detailRaw || error?.message || 'unknown');
        toast.error(language === 'es' ? `Error al leer imagen: ${detail}` : `Error reading image: ${detail}`);
      }
      awaitingResponseRef.current = false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendWithFile = async (message, file) => {
    if (!currentConversation) {
      // Create new conversation first
      if (onNewConversation) {
        await onNewConversation();
      }
      toast.error(t.errorSelectConversation);
      return;
    }

    setIsLoading(true);
    awaitingResponseRef.current = true;
    try {
      const formData = new FormData();
      formData.append('conversation_id', currentConversation.id);
      formData.append('message', message);
      formData.append('file', file);

      const token = getToken();
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults

      await axios.post(`${API}/chat/with-file`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (onRefreshConversation) {
        await onRefreshConversation(currentConversation.id);
      }

      toast.success(t.fileAnalyzed);
    } catch (error) {
      console.error('Error sending file:', error);
      toast.error(t.errorAnalyzeFile);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para analizar una imagen generada por IA
  const handleAnalyzeGeneratedImage = async (imageData) => {
    if (!currentConversation) {
      toast.error(t.errorSelectConversation);
      return;
    }

    setIsLoading(true);
    try {
      // Convert base64 to File
      const response = await fetch(`data:image/png;base64,${imageData.base64}`);
      const blob = await response.blob();
      const file = new File([blob], `imagen_generada_${Date.now()}.png`, { type: 'image/png' });

      const formData = new FormData();
      formData.append('conversation_id', currentConversation.id);
      formData.append('message', language === 'es' 
        ? `Analiza esta imagen que generé con el prompt: "${imageData.prompt}". ¿Qué puedes decirme sobre ella?`
        : `Analyze this image I generated with the prompt: "${imageData.prompt}". What can you tell me about it?`);
      formData.append('file', file);

      const token = getToken();
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults

      await axios.post(`${API}/chat/with-file`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (onRefreshConversation) {
        await onRefreshConversation(currentConversation.id);
      }

      toast.success(t.imageSentForAnalysis);
    } catch (error) {
      console.error('Error analyzing generated image:', error);
      toast.error(t.errorAnalyzeImage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler para enviar imagen al módulo Vision_Replication
  const handleSendImageForVision = async (imageData) => {
    // Si no hay conversación, crear una nueva automáticamente
    let convId = currentConversation?.id;
    
    if (!convId) {
      // Crear nueva conversación
      try {
        const token = getToken();
        const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
        
        const newConvResponse = await axios.post(`${API}/conversations`, 
          { title: t.newConversation },
          { headers }
        );
        
        convId = newConvResponse.data.id;
        
        // Seleccionar la nueva conversación para que se muestre en la UI
        if (onSelectConversation) {
          onSelectConversation(convId);
        }
        
        // Espera para asegurar que la conversación se guarde en BD
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error('Error creating conversation for image:', error);
        toast.error(t.errorCreatingConversation);
        return;
      }
    }

    setIsLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };  // P2.1 cookie-only via axios.defaults

      // Extraer el base64 puro (sin el prefijo data:image/...)
      const base64Content = imageData.content.includes(',') 
        ? imageData.content.split(',')[1] 
        : imageData.content;

      const imgLabel = language === 'es' ? 'Imagen' : 'Image';
      const imgUpLabel = language === 'es' ? 'Imagen subida' : 'Image uploaded';
      // OPTIMISTIC UPDATE: Mostrar imagen del usuario inmediatamente
      const tempUserMessage = {
        role: 'user',
        content: imageData.instruction 
          ? `[📷 ${imgLabel}: ${imageData.filename}]\n${imageData.instruction}`
          : `[📷 ${imgUpLabel}: ${imageData.filename}]`,
        timestamp: new Date().toISOString(),
        has_image: true,
        image_base64_preview: imageData.content  // Para mostrar preview temporal
      };
      
      // Actualizar mensajes localmente para mostrar la imagen inmediatamente
      if (currentConversation && onUpdateMessages) {
        // Usar el callback del padre para actualizar los mensajes
        const tempMessages = [...(currentConversation.messages || []), tempUserMessage];
        onUpdateMessages(tempMessages);
      }

      // Enviar al endpoint de Vision_Replication
      const response = await axios.post(`${API}/vision/process`, {
        image_base64: base64Content,
        instruction: imageData.instruction || '',
        conversation_id: convId
      }, { headers });

      // Mensaje del usuario con datos del servidor
      const userMessage = {
        role: 'user',
        content: imageData.instruction 
          ? `[📷 ${imgLabel}: ${imageData.filename}]\n${imageData.instruction}`
          : `[📷 ${imgUpLabel}: ${imageData.filename}]`,
        timestamp: new Date().toISOString(),
        has_image: true,
        image_id: response.data.image_id,
        image_url: response.data.image_url
      };

      // Agregar respuesta de DamyxIA
      const aiMessage = {
        role: 'assistant',
        content: response.data.response || response.data.message || 
                    t.imageProcessed,
        timestamp: new Date().toISOString(),
        vision_result: response.data
      };

      // Refrescar la conversación para obtener los datos actualizados del servidor
      if (onRefreshConversation) {
        await onRefreshConversation(convId);
      }

      // Mostrar toast según el resultado
      if (response.data.published) {
        toast.success(
          language === 'es' 
            ? '✨ Imagen publicada en el Área de Trabajo' 
            : '✨ Image published to Workspace'
        );
        // Disparar evento para que el Dashboard recargue las imágenes
        window.dispatchEvent(new CustomEvent('workspace-image-published'));
      } else {
        toast.success(
          language === 'es' 
            ? '👁️ Imagen analizada y lista para trabajar' 
            : '👁️ Image analyzed and ready to work'
        );
      }

    } catch (error) {
      console.error('Error sending image to Vision_Replication:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || t.errorProcessingImage;
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // DRAG & DROP HANDLERS
  // ============================================
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Solo ocultar si salimos del área de drop
    if (e.currentTarget === dropZoneRef.current && !e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // Verificar que sea una imagen
    if (!file.type.startsWith('image/')) {
      toast.error(t.onlyImages);
      return;
    }
    
    // Convertir a base64
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target?.result;
      if (!base64) return;
      
      // Mostrar preview temporal mientras se procesa
      setDraggedImage({
        base64: base64,
        filename: file.name,
        size: file.size
      });
      
      // Procesar la imagen automáticamente
      await handleSendImageForVision({
        content: base64,  // Corregido: usar 'content' en lugar de 'base64'
        filename: file.name,
        instruction: '' // Sin instrucción inicial, solo cargar
      });
      
      // Limpiar el estado de la imagen arrastrada
      setDraggedImage(null);
    };
    
    reader.readAsDataURL(file);
  };

  const handleCopyConversation = async (conv, e) => {
    e.stopPropagation();
    
    try {
      const token = getToken();
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      const response = await axios.get(`${API}/conversations/${conv.id}`, { headers });
      const fullConv = response.data;
      
      const messages = fullConv.messages || [];
      if (messages.length === 0) {
        toast.error(t.noMessagesForCopy);
        return;
      }

      const text = messages
        .map(msg => `${msg.role === 'user' ? t.user : 'DamyxIA'}: ${msg.content}`)
        .join('\n\n---\n\n');

      // Helper function for textarea fallback
      const copyWithTextarea = () => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px;opacity:0;';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          return successful;
        } catch (err) {
          document.body.removeChild(textArea);
          return false;
        }
      };

      // Try clipboard API first
      let copied = false;
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(text);
          copied = true;
        }
      } catch (clipErr) {
      }

      // Fallback to execCommand
      if (!copied) {
        copied = copyWithTextarea();
      }

      if (copied) {
        setCopiedConvId(conv.id);
        toast.success(t.conversationCopied);
        setTimeout(() => setCopiedConvId(null), 2000);
      } else {
        toast.error(t.couldNotCopy);
      }
    } catch (error) {
      toast.error(t.errorCopy);
    }
  };

  const handleExportConversation = async (conv, e) => {
    e.stopPropagation();
    
    try {
      const token = getToken();
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      const response = await axios.get(`${API}/conversations/${conv.id}`, { headers });
      const fullConv = response.data;
      
      const messages = fullConv.messages || [];
      if (messages.length === 0) {
        toast.error(t.noMessagesForExport);
        return;
      }

      const hasCode = messages.some(msg => msg.role === 'assistant' && msg.content && msg.content.includes('```'));
      
      if (hasCode) {
        const projectName = conv.title ? conv.title.replace(/[^a-zA-Z0-9]/g, '_') : (language === 'es' ? 'proyecto' : 'project');
        const fileCount = await downloadConversationAsProject(messages, projectName);
        toast.success(`${t.projectDownloaded} ${fileCount} ${t.files}`);
      } else {
        const text = messages
          .map(msg => `${msg.role === 'user' ? t.user : 'DamyxIA'}: ${msg.content}`)
          .join('\n\n---\n\n');
        
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${conv.title || (language === 'es' ? 'conversacion' : 'conversation')}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(t.conversationExported);
      }
    } catch (error) {
      toast.error(t.errorExport);
    }
  };

  const handleDownloadProject = async () => {
    if (!currentConversation) return;
    const messages = currentConversation.messages || [];
    if (messages.length === 0) {
      toast.error(t.noMessagesInConversation);
      return;
    }

    setIsDownloading(true);
    try {
      const projectName = currentConversation.title ? currentConversation.title.replace(/[^a-zA-Z0-9]/g, '_') : (language === 'es' ? 'proyecto' : 'project');
      const fileCount = await downloadConversationAsProject(messages, projectName);
      toast.success(`${t.projectDownloaded} ${fileCount} ${t.files}`);
    } catch (error) {
      toast.error(t.noCodeToDownload);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!currentConversation) return;
    const messages = currentConversation.messages || [];
    if (messages.length === 0) {
      toast.error(t.noMessagesForExport);
      return;
    }

    try {
      await exportConversationToPDF(currentConversation, language);
      toast.success(t.pdfExported);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(t.errorPDF);
    }
  };

  const handleCopyCurrentConversation = async () => {
    if (!currentConversation) return;
    const messages = currentConversation.messages || [];
    if (messages.length === 0) {
      toast.error(t.noMessagesForCopy);
      return;
    }

    const text = messages
      .map(msg => `${msg.role === 'user' ? t.user : 'DamyxIA'}: ${msg.content}`)
      .join('\n\n---\n\n');

    // Helper function for textarea fallback
    const copyWithTextarea = () => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px;opacity:0;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    };

    // Try modern clipboard API first
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success(t.conversationCopiedClipboard);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (error) {
    }

    // Fallback to execCommand
    if (copyWithTextarea()) {
      setCopied(true);
      toast.success(t.conversationCopiedClipboard);
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error(t.couldNotCopy);
    }
  };

  const checkHasCode = () => {
    if (!currentConversation) return false;
    const messages = currentConversation.messages || [];
    return messages.some(msg => msg.role === 'assistant' && msg.content && msg.content.includes('```'));
  };

  const extractCodeForPreview = () => {
    if (!currentConversation) return '';
    const messages = currentConversation.messages || [];
    
    // Find the last assistant message with code
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === 'assistant' && msg.content) {
        // Extract all code blocks with their language tags
        const codeBlockRegex = /```(jsx?|javascript|tsx?|react|js)?\n?([\s\S]*?)```/g;
        let match;
        const codeBlocks = [];
        let hasAppWithImports = false;
        
        while ((match = codeBlockRegex.exec(msg.content)) !== null) {
          const lang = match[1] || '';
          const code = match[2] || '';

          // 🟥 P26.33 · Nunca tratar el fence de acción de DamyxIA como JSX.
          if (isDamyxActionBlock(lang, code)) continue;

          // Skip non-JSX code blocks
          const lowerLang = lang.toLowerCase();
          if (['bash', 'sh', 'shell', 'css', 'scss', 'sass', 'json', 'yaml', 'yml', 'html', 'sql'].includes(lowerLang)) {
            continue;
          }
          
          // Skip shell commands and config
          if (code.trim().startsWith('npx ') || 
              code.trim().startsWith('npm ') || 
              code.trim().startsWith('yarn ') ||
              code.trim().startsWith('cd ') ||
              code.trim().startsWith('@tailwind') ||
              code.trim().startsWith('module.exports')) {
            continue;
          }
          
          // Check if it contains React code
          const hasReactCode = code.includes('function ') || 
                              code.includes('const ') ||
                              code.includes('return (') ||
                              code.includes('useState') ||
                              code.includes('<div');
          
          if (hasReactCode) {
            const isMainApp = code.includes('function App') || 
                             code.includes('const App') || 
                             code.includes('// src/App');
            const hasLocalImports = code.includes("from './") || code.includes("from \"./");
            if (isMainApp && hasLocalImports) {
              hasAppWithImports = true;
            }
            codeBlocks.push({ code, isMainApp, hasLocalImports });
          }
        }
        
        if (codeBlocks.length > 0) {
          // If there's an App with local imports and multiple files, this is a complex project
          // Try to combine only if there are enough components
          if (hasAppWithImports && codeBlocks.length > 2) {
            // Build a combined file with renamed variables to avoid collisions
            let counter = 0;
            const processedBlocks = [];
            
            for (const block of codeBlocks) {
              let processedCode = block.code;
              // Remove ALL import statements
              processedCode = processedCode.replace(/import\s+.*from\s+['"].*['"];?\n?/g, '');
              processedCode = processedCode.replace(/import\s+['"].*['"];?\n?/g, '');
              // Remove exports
              processedCode = processedCode.replace(/export\s+default\s+/g, '');
              processedCode = processedCode.replace(/export\s+{\s*[\w\s,]*\s*};?\n?/g, '');
              processedCode = processedCode.replace(/export\s+const\s+/g, 'const ');
              processedCode = processedCode.replace(/export\s+function\s+/g, 'function ');
              // Remove file path comments
              processedCode = processedCode.replace(/^\/\/\s*src\/.*\n?/gm, '');
              
              // Rename 'data' variable to unique name to avoid collisions
              // But only for non-App components
              if (!block.isMainApp) {
                counter++;
                processedCode = processedCode.replace(/const data = /g, `const data${counter} = `);
                // Also update references to data in the same block
                // This is tricky, so we use a simple approach: rename the variable definition
              }
              
              processedBlocks.push({ code: processedCode.trim(), isMainApp: block.isMainApp });
            }
            
            // Sort to put App last (so it can reference other components)
            processedBlocks.sort((a, b) => (a.isMainApp ? 1 : 0) - (b.isMainApp ? 1 : 0));
            
            return processedBlocks.map(b => b.code).join('\n\n');
          }
          
          // For simpler cases, just use the App or the first component
          const appBlock = codeBlocks.find(b => b.isMainApp);
          const blockToUse = appBlock || codeBlocks[0];
          
          let processedCode = blockToUse.code;
          // Remove ALL import statements
          processedCode = processedCode.replace(/import\s+.*from\s+['"].*['"];?\n?/g, '');
          processedCode = processedCode.replace(/import\s+['"].*['"];?\n?/g, '');
          // Remove exports
          processedCode = processedCode.replace(/export\s+default\s+/g, '');
          processedCode = processedCode.replace(/export\s+{\s*[\w\s,]*\s*};?\n?/g, '');
          processedCode = processedCode.replace(/export\s+const\s+/g, 'const ');
          processedCode = processedCode.replace(/export\s+function\s+/g, 'function ');
          // Remove file path comments
          processedCode = processedCode.replace(/^\/\/\s*src\/.*\n?/gm, '');
          
          return processedCode.trim();
        }
      }
    }
    return '';
  };

  const hasCode = checkHasCode();
  const messages = currentConversation ? (currentConversation.messages || []) : [];
  const convList = conversations || [];

  // 🟥 CUADRO NEGRO rev6 — Reset de overrides en cada turno.
  //   - Al cambiar de conversación: ambos sets se vacían.
  //   - Al crecer el contador de mensajes: `userClosed` se vacía
  //     (último 3 vuelven a abrirse) y `userOpened` se PRESERVA.
  useEffect(() => {
    const len = messages.length;
    const convId = currentConversation?.id || null;
    const convChanged = convId !== prevConvIdRef.current;
    const lenChanged = len !== prevMsgLenRef.current;
    if (convChanged) {
      setUserOpened(new Set());
      setUserClosed(new Set());
    } else if (lenChanged) {
      setUserClosed(new Set());
    }
    prevMsgLenRef.current = len;
    prevConvIdRef.current = convId;
  }, [messages.length, currentConversation?.id]);

  // Helper: ¿está expandido el mensaje en `idx` dado un total?
  const isMessageExpanded = useCallback((idx, total) => {
    const lastOpenStart = Math.max(0, total - LAST_OPEN_COUNT);
    const isInLastN = idx >= lastOpenStart;
    if (userClosed.has(idx)) return false;       // cerrado manualmente este render
    if (isInLastN) return true;                  // últimos 3 siempre abiertos
    if (userOpened.has(idx)) return true;        // abierto manualmente (sobrevive)
    return false;
  }, [userClosed, userOpened]);

  // Helper: toggle manual del usuario.
  const toggleMessageExpansion = useCallback((idx, total) => {
    const lastOpenStart = Math.max(0, total - LAST_OPEN_COUNT);
    const isInLastN = idx >= lastOpenStart;
    const currentlyOpen = isMessageExpanded(idx, total);
    if (currentlyOpen) {
      // Cerrar este mensaje.
      if (isInLastN) {
        setUserClosed(prev => new Set(prev).add(idx));
      } else {
        // Si lo cerraron y estaba en userOpened, quitarlo.
        setUserOpened(prev => {
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });
      }
    } else {
      // Abrir este mensaje.
      if (isInLastN) {
        setUserClosed(prev => {
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });
      } else {
        setUserOpened(prev => new Set(prev).add(idx));
      }
    }
  }, [isMessageExpanded]);

  // Track message count for workspace sync
  useEffect(() => {
    if (!currentConversation || !messages.length) {
      lastMessageCountRef.current = 0;
      return;
    }
    
    const currentMsgCount = messages.length;
    const prevMsgCount = lastMessageCountRef.current;
    
    // Check if new messages were added
    if (currentMsgCount > prevMsgCount && currentMsgCount >= 2) {
      const lastMsg = messages[messages.length - 1];
      
      // If the last message is from assistant and contains JSX/React code
      if (lastMsg?.role === 'assistant' && lastMsg?.content) {
        const content = lastMsg.content;
        const hasJSXCode = content.includes('```jsx') || 
                          content.includes('```javascript') || 
                          content.includes('function App') ||
                          content.includes('const App') ||
                          (content.includes('```') && content.includes('return ('));
        
        // Code is now displayed in the Workspace area (Dashboard component)
        // No popup preview - everything stays in the workspace
      }
    }
    
    lastMessageCountRef.current = currentMsgCount;
  }, [messages, currentConversation, language]); // eslint-disable-line react-hooks/exhaustive-deps

  // Simular progreso del workflow cuando se envía un mensaje
  useEffect(() => {
    if (isLoading) {
      setIsProcessing(true);
      setWorkflowStep(1);
      const timer1 = setTimeout(() => setWorkflowStep(2), 500);
      const timer2 = setTimeout(() => setWorkflowStep(3), 1200);
      const timer3 = setTimeout(() => setWorkflowStep(4), 2000);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    } else {
      if (workflowStep > 0) {
        setWorkflowStep(5);
        setTimeout(() => {
          setIsProcessing(false);
          setProductivityMetrics(prev => ({
            ...prev,
            components: prev.components + 1,
            lines: prev.lines + Math.floor(Math.random() * 50) + 10
          }));
        }, 500);
      }
    }
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-full flex flex-col bg-zinc-950/30 light:bg-zinc-50/50 mr-2">
      {/* Header - Panel Izquierdo con diseño DamyxIA */}
      <MindPanelHeader 
        isProcessing={isProcessing} 
        onCloseConversation={onCloseConversation}
        onRefreshConversation={() => {
          if (currentConversation && onRefreshConversation) {
            onRefreshConversation(currentConversation.id);
          }
        }}
        onExpandPanel={onExpandPanel}
        isExpanded={isExpanded}
        onOpenHomepage={() => setShowHomepageModal(true)}
        onOpenGroupsProjects={onOpenGroupsProjects}
        onOpenServicesInfo={onOpenServicesInfo}
        onOpenStepPanel={onOpenStepPanel}
        onAttachFile={onAttachFile}
        onOpenImageGenerator={onOpenImageGenerator}
        onOpenDiagramGenerator={onOpenDiagramGenerator}
        onInstallApp={onInstallApp}
        onOpenImageGallery={onOpenImageGallery}
      />

      {/* Workflow + Métricas + Rollback + Historial (P3.1 → ChatToolbar). */}
      <ChatToolbar
        isProcessing={isProcessing}
        workflowStep={workflowStep}
        productivityMetrics={productivityMetrics}
        showWorkflowDesktop={showWorkflowDesktop}
        setShowWorkflowDesktop={setShowWorkflowDesktop}
        showMetricsDesktop={showMetricsDesktop}
        setShowMetricsDesktop={setShowMetricsDesktop}
        showWorkflow={showWorkflow}
        setShowWorkflow={setShowWorkflow}
        showMetrics={showMetrics}
        setShowMetrics={setShowMetrics}
        handleRevertClick={handleRevertClick}
        reverting={reverting}
        hasSnapshots={chatSnapshotCount > 0 || Boolean(activeModuleId) || ((currentConversation?.messages || []).length > 0)}
        onOpenConversationsModal={onOpenConversationsModal}
        activeModuleId={activeModuleId}
        activeModuleName={activeModuleName}
        t={t}
      />

      {/* 🟩 P26.6 · Builder toggle ELIMINADO por mandato del owner.
        * El builder SIEMPRE está activo — DamyxIA crea proyectos en el
        * Workspace cuando detecta verbos de creación ("crear", "construir",
        * "hacer", "build", "make", "create", etc.). El componente
        * BuilderToggle queda archivado para retro-compat pero no se
        * renderiza más en el chat.
        */}

      {/* Chat Messages Area - con Drag & Drop para imágenes (P3.1 → ChatMessages). */}
      <ChatMessages
        dropZoneRef={dropZoneRef}
        messagesEndRef={messagesEndRef}
        messages={messages}
        conversationId={currentConversation?.id}
        isLoading={isLoading}
        awaitingResponseRef={awaitingResponseRef}
        isDragging={isDragging}
        draggedImage={draggedImage}
        handleDragEnter={handleDragEnter}
        handleDragLeave={handleDragLeave}
        handleDragOver={handleDragOver}
        handleDrop={handleDrop}
        expandedIdx={null}
        setExpandedIdx={null}
        isMessageExpanded={isMessageExpanded}
        toggleMessageExpansion={toggleMessageExpansion}
        detectMessageIcon={detectMessageIcon}
        getIconTooltip={getIconTooltip}
        t={t}
        onUpdateMessages={onUpdateMessages}
        onGoToWorkspace={onGoToWorkspace}
        onIntentDecision={handleIntentDecision}
      />

      {/* Style Indicator - Solo visible en modo desarrollador */}
      {devModeEnabled && currentProfile && messages.length > 0 && (
        <div className="px-3 py-1.5 border-t border-zinc-800/30 bg-zinc-900/20 flex items-center justify-between">
          <span className="text-xs text-zinc-500">
                        {t.responseMode}
          </span>
          <StyleIndicator 
            profile={currentProfile} 
            styleWasManual={styleWasManual}
            levelWasManual={levelWasManual}
          />
        </div>
      )}
      
      {/* Active Modules Indicator - Solo visible en modo desarrollador */}
      {devModeEnabled && activeModules && activeModules.length > 0 && messages.length > 0 && (
        <div className="px-3 py-1.5 border-t border-zinc-800/30 bg-zinc-900/30">
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500 mr-2">
                            {t.activeModules}
            </span>
            <ActiveModulesIndicator 
              modules={activeModules} 
              onOpenPanel={() => setShowModulesPanel(true)}
            />
          </div>
        </div>
      )}

      {/* Chat Input with Download/Copy buttons */}
      {/* 🟪 P25.9 · mt-auto empuja este wrapper al fondo del flex-col
          para que el composer quede pegado a la BottomNav en mobile. */}
      <div className="mt-auto border-t border-zinc-800/50 light:border-zinc-200">
        {/* Show only code-related actions if there's code */}
        {messages.length > 0 && hasCode && (
          <div className="flex items-center justify-end gap-2 px-4 py-2 bg-zinc-900/30 light:bg-zinc-100/50">
              <button
                onClick={handleDownloadProject}
                disabled={isDownloading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-indigo-400 hover:text-indigo-300 bg-indigo-600/20 hover:bg-indigo-600/30 rounded-lg transition-colors disabled:opacity-50"
                title={t.downloadCode}
              >
                <FolderDown className="w-3.5 h-3.5" />
                {isDownloading ? t.downloading : t.downloadProject}
              </button>
          </div>
        )}
        
        <ChatInput 
          onSendMessage={handleSendMessage}
          onSendWithFile={handleSendWithFile}
          onSendImageForVision={handleSendImageForVision}
          onReadImageWithVision={handleReadImageWithVision}
          isLoading={isLoading}
          conversationId={currentConversation?.id}
          onImageGenerated={() => {
            if (currentConversation?.id && onRefreshConversation) {
              onRefreshConversation(currentConversation.id);
            }
          }}
          onAnalyzeGeneratedImage={handleAnalyzeGeneratedImage}
          onOpenGroupsProjects={onOpenGroupsProjects}
          onOpenServicesInfo={onOpenServicesInfo}
          onOpenStepPanel={onOpenStepPanel}
          showQuickActions={!!(onOpenGroupsProjects || onOpenServicesInfo || onOpenStepPanel)}
        />
      </div>
      
      {/* Share Conversation Modal */}
      <ShareConversationModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        conversationId={currentConversation?.id}
        conversationTitle={currentConversation?.title}
      />
      
      {/* Active Modules Panel - Detailed view of active modules */}
      <ActiveModulesPanel
        modules={activeModules}
        isOpen={showModulesPanel}
        onClose={() => setShowModulesPanel(false)}
      />
      
      {/* Rollback Modal - Fase 4.2 */}
      <RollbackModal
        isOpen={showRollbackModal}
        onClose={() => setShowRollbackModal(false)}
        onRollbackComplete={(data) => {
          // Aquí se puede agregar lógica adicional después del rollback
          // Por ejemplo, refrescar la conversación o el workspace
          if (onRefreshConversation) {
            onRefreshConversation();
          }
        }}
      />
      
      {/* Homepage Modal - Guía de DamyxIA */}
      <HomepageModal
        isOpen={showHomepageModal}
        onClose={() => setShowHomepageModal(false)}
      />
    </div>
  );
};

export default ChatPanel;
